import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaBuilding,
  FaAddressCard,
  FaIdCard,
} from "react-icons/fa";

const CustomerManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    type: "individual",
    name: "",
    address: "",
    contact: "",
    fax: "",
    email: "",
    url: "",
    gst_no: "",
    ntn: "",
    contacts: [],
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/assets/customers`,
        {
          credentials: "include",
        }
      );
      const data = await response.json();
      setCustomers(data.customers || []);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
    setIsLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    try {
      const url = editingCustomer
        ? `${process.env.REACT_APP_BASE_URL}/api/assets/customers/${editingCustomer.id}`
        : `${process.env.REACT_APP_BASE_URL}/api/assets/customers`;
      const method = editingCustomer ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          ...formData,
        }),
      });

      if (response.ok) {
        alert(
          `Customer ${editingCustomer ? "updated" : "created"} successfully!`
        );
        setFormData({
          type: "individual",
          name: "",
          address: "",
          contact: "",
          fax: "",
          email: "",
          url: "",
          gst_no: "",
          ntn: "",
          contacts: [],
        });
        setEditingCustomer(null);
        setShowModal(false);
        fetchCustomers();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error("Error saving customer:", error);
      alert("Error saving customer");
    }
    setIsLoading(false);
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      type: customer.type,
      name: customer.name,
      address: customer.address || "",
      contact: customer.contact || "",
      fax: customer.fax || "",
      email: customer.email || "",
      url: customer.url || "",
      gst_no: customer.gst_no || "",
      ntn: customer.ntn || "",
      contacts: customer.contacts || [],
    });
    setShowModal(true);
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm("Are you sure you want to delete this customer?")) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/assets/customers/${customerId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (response.ok) {
        alert("Customer deleted successfully!");
        fetchCustomers();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error("Error deleting customer:", error);
      alert("Error deleting customer");
    }
  };

  const openAddModal = () => {
    setFormData({
      type: "individual",
      name: "",
      address: "",
      contact: "",
      fax: "",
      email: "",
      url: "",
      gst_no: "",
      ntn: "",
      contacts: [],
    });
    setEditingCustomer(null);
    setShowModal(true);
  };

  const addContact = () => {
    setFormData({
      ...formData,
      contacts: [
        ...formData.contacts,
        {
          name: "",
          contact: "",
          fax: "",
          email: "",
          address: "",
          project: "",
        },
      ],
    });
  };

  const updateContact = (index, field, value) => {
    const updatedContacts = [...formData.contacts];
    updatedContacts[index][field] = value;
    setFormData({
      ...formData,
      contacts: updatedContacts,
    });
  };

  const removeContact = (index) => {
    const updatedContacts = formData.contacts.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      contacts: updatedContacts,
    });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
          Customer Management
        </h2>
        <p className="mt-3 text-lg text-slate-600 max-w-2xl mx-auto">
          Manage your business customers and their contact information
        </p>
      </div>

      {/* Main Content */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-slate-200/60 shadow-lg">
        <div className="p-8 border-b border-slate-200/60">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600">
                <FaUser className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-800">Customers</h3>
                <p className="text-slate-600">
                  {customers.length}{" "}
                  {customers.length === 1 ? "customer" : "customers"} registered
                </p>
              </div>
            </div>
            <button
              className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 font-semibold shadow-lg shadow-blue-500/25 transition-all duration-200"
              onClick={openAddModal}
            >
              <FaPlus className="w-4 h-4" />
              <span>Add Customer</span>
            </button>
          </div>
        </div>

        <div className="p-8">
          {isLoading ? (
            <div className="text-center py-8">Loading customers...</div>
          ) : customers.length === 0 ? (
            <div className="text-center py-16">
              <div className="p-6 bg-slate-100 rounded-full w-fit mx-auto mb-6">
                <FaUser className="w-12 h-12 text-slate-400" />
              </div>
              <h4 className="text-2xl font-bold text-slate-800 mb-4">
                No Customers Found
              </h4>
              <p className="text-slate-600 max-w-md mx-auto leading-relaxed mb-8">
                You haven't added any customers yet. Start by creating your
                first customer.
              </p>
              <button
                className="px-8 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 font-semibold shadow-lg shadow-blue-500/25 transition-all duration-200"
                onClick={openAddModal}
              >
                Create Your First Customer
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {customers.map((customer) => (
                <div
                  key={customer.id}
                  className="bg-white/80 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <FaBuilding className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <h4 className="text-lg font-bold text-slate-800">
                            {customer.name}
                          </h4>
                          <p className="text-sm text-slate-600 capitalize">
                            {customer.type}
                          </p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(customer)}
                          className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(customer.id)}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="bg-slate-50/80 rounded-lg p-4 space-y-2 mb-4">
                      {customer.contact && (
                        <div className="flex items-center space-x-2">
                          <FaPhone className="w-4 h-4 text-slate-500" />
                          <span className="text-sm text-slate-700">
                            {customer.contact}
                          </span>
                        </div>
                      )}
                      {customer.email && (
                        <div className="flex items-center space-x-2">
                          <FaEnvelope className="w-4 h-4 text-slate-500" />
                          <span className="text-sm text-slate-700">
                            {customer.email}
                          </span>
                        </div>
                      )}
                      {customer.address && (
                        <div className="flex items-center space-x-2">
                          <FaAddressCard className="w-4 h-4 text-slate-500" />
                          <span className="text-sm text-slate-700">
                            {customer.address}
                          </span>
                        </div>
                      )}
                      {(customer.gst_no || customer.ntn) && (
                        <div className="flex items-center space-x-2">
                          <FaIdCard className="w-4 h-4 text-slate-500" />
                          <span className="text-sm text-slate-700">
                            {customer.gst_no && `GST: ${customer.gst_no}`}
                            {customer.gst_no && customer.ntn && " | "}
                            {customer.ntn && `NTN: ${customer.ntn}`}
                          </span>
                        </div>
                      )}
                    </div>

                    {customer.contacts && customer.contacts.length > 0 && (
                      <div>
                        <h5 className="text-sm font-semibold text-slate-700 mb-2">
                          Contacts ({customer.contacts.length})
                        </h5>
                        <div className="space-y-2">
                          {customer.contacts.map((contact, index) => (
                            <div
                              key={index}
                              className="bg-slate-100/80 rounded-lg p-3"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <FaUser className="w-3 h-3 text-slate-500" />
                                  <span className="text-sm font-medium text-slate-800">
                                    {contact.name}
                                  </span>
                                </div>
                                {contact.contact && (
                                  <span className="text-xs text-slate-600">
                                    {contact.contact}
                                  </span>
                                )}
                              </div>
                              {contact.project && (
                                <p className="text-xs text-slate-500 mt-1">
                                  Project: {contact.project}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-800">
                  {editingCustomer ? "Edit Customer" : "Add New Customer"}
                </h3>
                <button
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCustomer(null);
                    setFormData({
                      type: "individual",
                      name: "",
                      address: "",
                      contact: "",
                      fax: "",
                      email: "",
                      url: "",
                      gst_no: "",
                      ntn: "",
                      contacts: [],
                    });
                  }}
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Customer Type */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">
                  Customer Type *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({ ...formData, type: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
                  required
                >
                  <option value="individual">Individual</option>
                  <option value="business">Business</option>
                </select>
              </div>

              {/* Basic Information */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-slate-700">
                    Customer Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="Enter customer name"
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-slate-700">
                    Contact Number
                  </label>
                  <input
                    type="tel"
                    value={formData.contact}
                    onChange={(e) =>
                      setFormData({ ...formData, contact: e.target.value })
                    }
                    placeholder="Enter contact number"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  placeholder="Enter email address"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">
                  Address
                </label>
                <textarea
                  value={formData.address}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                  placeholder="Enter full address"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
                  rows="3"
                />
              </div>

              {/* Business Details */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-slate-700">
                    GST Number
                  </label>
                  <input
                    type="text"
                    value={formData.gst_no}
                    onChange={(e) =>
                      setFormData({ ...formData, gst_no: e.target.value })
                    }
                    placeholder="Enter GST number"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-slate-700">
                    NTN
                  </label>
                  <input
                    type="text"
                    value={formData.ntn}
                    onChange={(e) =>
                      setFormData({ ...formData, ntn: e.target.value })
                    }
                    placeholder="Enter NTN"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
                  />
                </div>
              </div>

              {/* Additional Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-slate-700">
                    Fax
                  </label>
                  <input
                    type="text"
                    value={formData.fax}
                    onChange={(e) =>
                      setFormData({ ...formData, fax: e.target.value })
                    }
                    placeholder="Enter fax number"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-slate-700">
                    Website
                  </label>
                  <input
                    type="url"
                    value={formData.url}
                    onChange={(e) =>
                      setFormData({ ...formData, url: e.target.value })
                    }
                    placeholder="Enter website URL"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
                  />
                </div>
              </div>

              {/* Customer Contacts */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold text-slate-800">
                    Customer Contacts
                  </h4>
                  <button
                    type="button"
                    onClick={addContact}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-100 text-green-700 font-medium rounded-lg hover:bg-green-200 transition-colors duration-200"
                  >
                    <FaPlus className="w-4 h-4" />
                    <span>Add Contact</span>
                  </button>
                </div>

                {formData.contacts.map((contact, index) => (
                  <div
                    key={index}
                    className="border border-slate-200 rounded-lg p-4 space-y-4"
                  >
                    <div className="flex items-center justify-between">
                      <h5 className="font-medium text-slate-700">
                        Contact {index + 1}
                      </h5>
                      <button
                        type="button"
                        onClick={() => removeContact(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                        title="Remove contact"
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="block text-sm font-semibold text-slate-700">
                          Contact Name *
                        </label>
                        <input
                          type="text"
                          value={contact.name}
                          onChange={(e) =>
                            updateContact(index, "name", e.target.value)
                          }
                          placeholder="Enter contact name"
                          required
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-semibold text-slate-700">
                          Contact Number
                        </label>
                        <input
                          type="tel"
                          value={contact.contact}
                          onChange={(e) =>
                            updateContact(index, "contact", e.target.value)
                          }
                          placeholder="Enter contact number"
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <label className="block text-sm font-semibold text-slate-700">
                          Email
                        </label>
                        <input
                          type="email"
                          value={contact.email}
                          onChange={(e) =>
                            updateContact(index, "email", e.target.value)
                          }
                          placeholder="Enter email"
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-semibold text-slate-700">
                          Fax
                        </label>
                        <input
                          type="text"
                          value={contact.fax}
                          onChange={(e) =>
                            updateContact(index, "fax", e.target.value)
                          }
                          placeholder="Enter fax number"
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-semibold text-slate-700">
                          Project
                        </label>
                        <input
                          type="text"
                          value={contact.project}
                          onChange={(e) =>
                            updateContact(index, "project", e.target.value)
                          }
                          placeholder="Enter project name"
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-slate-700">
                        Address
                      </label>
                      <textarea
                        value={contact.address}
                        onChange={(e) =>
                          updateContact(index, "address", e.target.value)
                        }
                        placeholder="Enter address"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
                        rows="2"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="button"
                  className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 font-semibold transition-colors"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCustomer(null);
                    setFormData({
                      type: "individual",
                      name: "",
                      address: "",
                      contact: "",
                      fax: "",
                      email: "",
                      url: "",
                      gst_no: "",
                      ntn: "",
                      contacts: [],
                    });
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 font-semibold shadow-lg shadow-blue-500/25 transition-all duration-200 disabled:opacity-50"
                >
                  <FaSave className="w-4 h-4" />
                  <span>{editingCustomer ? "Update" : "Create"} Customer</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerManagement;
